import { createFileRoute } from "@tanstack/react-router"
import { Check, X, Zap, AlertCircle, ArrowRight } from "lucide-react"
import { Button } from "@/components/ui/button"
import { LifetimeAccessPricing } from "@/components/life-time-access-pricing"
import { createLLMOMeta, createBreadcrumbSchema, createFAQSchema, createArticleSchema } from "@/lib/seo"
import { useAuth } from "@/components/auth/AuthProvider"
import { useAnalytics } from "@/lib/analytics"
import { Paragraph, ContentSection } from "@/components/tools/SeoContent"
import { useState } from "react"
import { AskAIExplain } from "@/components/ask-ai-to-explain"

// ─── FAQ Data ────────────────────────────────────────────────
const faqs = [
  {
    question: "Is Zapify better than LinktoDM?",
    answer:
      "Zapify is better for Instagram-first creators who want AI replies, bulk DM automation, a $0 free plan for 30 days or 1K contacts, Story DM automation, follower and lead attribution, detailed insights, templates, smart offers, language switching, and creator-friendly pricing. LinktoDM may fit users who specifically want a visual flow builder, Backtrack, Story Mention Auto Reply, DM queue controls, or Enterprise creator-management features.",
  },
  {
    question: "What is the best LinktoDM alternative?",
    answer:
      "Zapify is a strong LinktoDM alternative for creators who want AI-powered Instagram automation, bulk DMs, detailed insights, followers and leads gained through automation, ask-to-follow flows, email capture, templates, smart offers, and a focused Instagram growth workflow.",
  },
  {
    question: "Why switch from LinktoDM to Zapify?",
    answer:
      "Switch to Zapify if you want a focused Instagram growth platform instead of a more flow-builder-heavy automation setup. Zapify gives creators AI replies, bulk DM workflows, Zapify insights, smart offers, templates, language switching, 30-day free-plan value, and a possible lifetime access offer.",
  },
  {
    question: "Does Zapify support bulk DMs?",
    answer:
      "Yes. Zapify supports bulk DM workflows through Instagram comment-to-DM, Reel automation, Story DM automation, selected-post automation, and any-post automation. The supplied Zapify brief lists unlimited DMs and unlimited automations on paid plans.",
  },
  {
    question: "Does LinktoDM have a free plan?",
    answer:
      "Yes. LinktoDM publicly lists a Free plan with 2,000 contacts, 3 creator accounts, and unlimited automations. Its pricing table also shows several advanced features gated from Free.",
  },
  {
    question: "Does Zapify have a free plan?",
    answer:
      "Yes. Zapify's free plan is $0 for 30 days or 1K contacts.",
  },
  {
    question: "Does Zapify have AI replies?",
    answer:
      "Yes. Zapify is positioned as an AI-powered Instagram comment management and automation platform with AI replies and brand voice controls.",
  },
  {
    question: "Does LinktoDM have AI replies?",
    answer:
      "Yes. LinktoDM publicly lists AI auto reply and AI intent trigger, but its pricing table shows those features gated from Free.",
  },
  {
    question: "How does Zapify pricing compare with LinktoDM?",
    answer:
      "Zapify has a $0 free plan for 30 days or 1K contacts, paid plans listed at $9.99/month and $19.99/month in the brand brief, and a limited $200 lifetime access offer. LinktoDM publicly lists Free, Pro at $10/month yearly or $15/month monthly, Pro+ at $18/month yearly or $25/month monthly, and Enterprise custom pricing. Zapify has the stronger value story if the lifetime offer is live.",
  },
  {
    question: "Can I switch from LinktoDM to Zapify?",
    answer:
      "Yes. Zapify is a good switch if your main workflows are Instagram comment-to-DM, Story DMs, bulk DMs, AI replies, lead capture, ask-to-follow, templates, smart offers, follower growth, and attribution. Before switching, check whether you rely on LinktoDM-specific features like Flow Builder, Backtrack, Story Mention Auto Reply, DM queue controls, or Enterprise creator-management tools.",
  },
  {
    question: "Who should still consider LinktoDM?",
    answer:
      "LinktoDM may still make sense if you specifically want its visual flow builder, Backtrack, Story Mention Auto Reply, DM queue controls, shared media trigger, AI policy check, or Enterprise creator-management dashboard. For most Instagram-first creators who want growth attribution and smart campaign setup, Zapify is the better fit.",
  },
]

// ─── Feature Table Data ──────────────────────────────────────
type FeatureRow = {
  name: string
  zapify: string | boolean
  linktodm: string | boolean
  zapifyNote?: string
  linktodmNote?: string
}

type FeatureCategory = {
  category: string
  rows: FeatureRow[]
}

const featureCategories: FeatureCategory[] = [
  {
    category: "Core Automation",
    rows: [
      { name: "Instagram comment-to-DM automation", zapify: true, linktodm: true, zapifyNote: "Send DMs from comments with links, offers, and content", linktodmNote: "Public support docs cover Comment-to-DM" },
      { name: "Bulk DM automation", zapify: true, linktodm: true, zapifyNote: "Unlimited DMs and automations on paid plans", linktodmNote: "Unlimited automations; contacts limited on Free" },
      { name: "Unlimited DMs on paid plans", zapify: true, linktodm: false, zapifyNote: "Listed in Zapify brief", linktodmNote: "Contacts and automation focused; verify DM wording" },
      { name: "Single post automation", zapify: true, linktodm: true },
      { name: "Multiple selected posts", zapify: true, linktodm: false, linktodmNote: "Not publicly confirmed" },
      { name: "Any-post automation", zapify: true, linktodm: false, linktodmNote: "Not publicly confirmed" },
      { name: "Reel automation", zapify: true, linktodm: true },
      { name: "Story DM automation", zapify: true, linktodm: true, linktodmNote: "Story reply automation listed" },
      { name: "Story mention auto-reply", zapify: false, linktodm: true, zapifyNote: "Not publicly confirmed", linktodmNote: "Feature table lists Story Mention Auto Reply" },
      { name: "Keyword triggers", zapify: true, linktodm: true },
      { name: "Ask-to-follow / follow gate", zapify: true, linktodm: true, linktodmNote: "Feature table shows gate on Free" },
      { name: "Email capture in DMs", zapify: true, linktodm: true, linktodmNote: "Feature table shows gate on Free" },
    ],
  },
  {
    category: "AI & Intelligence",
    rows: [
      { name: "AI-powered replies", zapify: true, linktodm: true, zapifyNote: "AI replies with brand voice controls", linktodmNote: "AI Auto Reply gated on Free" },
      { name: "Brand voice controls", zapify: true, linktodm: false, zapifyNote: "Tone, length, emojis, hashtags", linktodmNote: "Not clearly promoted as Zapify-style controls" },
      { name: "Templates for quick setup", zapify: true, linktodm: true, linktodmNote: "Ready-to-use automation templates" },
      { name: "Smart offers / link cards", zapify: true, linktodm: false, linktodmNote: "Not clearly promoted" },
    ],
  },
  {
    category: "Analytics & Growth",
    rows: [
      { name: "Detailed Reel/post insights", zapify: true, linktodm: false, zapifyNote: "Reach, views, likes, comments, shares, saves, visits, taps, engagement", linktodmNote: "Performance tracking; no Zapify-style attribution" },
      { name: "Followers gained attribution", zapify: true, linktodm: false, zapifyNote: "Zapify insights" },
      { name: "Leads gained attribution", zapify: true, linktodm: false, zapifyNote: "Zapify insights", linktodmNote: "Data collection exists; attribution not confirmed" },
    ],
  },
  {
    category: "Platform & Pricing",
    rows: [
      { name: "Free plan", zapify: "$0 for 30 days or 1K contacts", linktodm: "$0 with 2,000 contacts, 3 creator accounts" },
      { name: "Starting paid plan", zapify: "$9.99/mo", linktodm: "$10/mo yearly or $15/mo monthly" },
      { name: "Higher paid plan", zapify: "$19.99/mo Professional", linktodm: "Pro+ $18/mo yearly or $25/mo monthly" },
      { name: "Lifetime access", zapify: "$200 once", linktodm: "Not available" },
      { name: "Multiple Instagram accounts", zapify: true, linktodm: true, zapifyNote: "Up to 6 on Professional", linktodmNote: "3 on Free/Pro, 8 on Pro+" },
      { name: "Team access", zapify: true, linktodm: true, zapifyNote: "Plan dependent", linktodmNote: "Up to 2/5/10 team members by plan" },
      { name: "Language switching", zapify: true, linktodm: false, zapifyNote: "10+ languages", linktodmNote: "Product language switching not confirmed" },
      { name: "Flow builder", zapify: false, linktodm: true, zapifyNote: "Not core Zapify positioning", linktodmNote: "Node-based Flow Builder" },
      { name: "Backtrack / missed interactions", zapify: false, linktodm: true, zapifyNote: "Not publicly confirmed", linktodmNote: "Feature table shows gate on Free" },
      { name: "Referral / affiliate program", zapify: true, linktodm: true, zapifyNote: "20% recurring commission", linktodmNote: "Affiliate program exists; verify terms" },
      { name: "Meta approval / partner status", zapify: true, linktodm: true, zapifyNote: "Approved by Meta", linktodmNote: "Meta Business Partner positioning" },
    ],
  },
]

// ─── Pricing Data ────────────────────────────────────────────
const pricingTiers = [
  { label: "Free plan", zapify: "$0 for 30 days or 1K contacts", linktodm: "$0 with 2,000 contacts, 3 creator accounts", zapifyWidth: "2%", linktodmWidth: "2%" },
  { label: "Entry paid plan", zapify: "$9.99/mo (Starter)", linktodm: "Pro $10/mo yearly or $15/mo monthly", zapifyWidth: "20%", linktodmWidth: "30%" },
  { label: "Higher paid plan", zapify: "$19.99/mo (Professional)", linktodm: "Pro+ $18/mo yearly or $25/mo monthly", zapifyWidth: "38%", linktodmWidth: "50%" },
  { label: "Lifetime access", zapify: "$200 once", linktodm: "Not available", zapifyWidth: "30%", linktodmWidth: "0%" },
  { label: "Enterprise", zapify: "Not applicable", linktodm: "Custom pricing", zapifyWidth: "0%", linktodmWidth: "20%" },
]

// ─── Route ───────────────────────────────────────────────────
export const Route = createFileRoute("/comparison/linktodm-vs-zapify-pro")({
  head: () => ({
    meta: [
      ...createLLMOMeta({
        title: "LinktoDM vs Zapify: Best LinktoDM Alternative for Instagram DM Automation",
        description:
          "Compare LinktoDM vs Zapify for Instagram DM automation, AI replies, bulk DMs, Story DMs, pricing, insights, and creator growth. See why creators choose Zapify.",
        url: "https://zapify.pro/comparison/linktodm-vs-zapify-pro",
        publishedTime: "2026-06-09",
        modifiedTime: "2026-06-09",
        ogType: "article",
      }),
      { property: "og:image", content: "https://cms.zapify.pro/uploads/OG_image1_f82f5871e2.png" },
      { property: "og:locale", content: "en_US" },
      { name: "keywords", content: "LinktoDM vs Zapify, Zapify vs LinktoDM, LinktoDM alternative, Instagram DM automation tool, Instagram comment to DM automation, bulk Instagram DM automation, AI Instagram replies, Story DM automation, Instagram automation for creators, affordable Instagram DM automation, best LinktoDM alternative" },
    ],
    links: [
      { rel: "canonical", href: "https://zapify.pro/comparison/linktodm-vs-zapify-pro" },
    ],
    scripts: [
      createBreadcrumbSchema({
        itemListElement: [
          { position: 1, name: "Home", item: "https://zapify.pro" },
          { position: 2, name: "Comparisons", item: "https://zapify.pro/comparison" },
          { position: 3, name: "LinktoDM vs Zapify", item: "https://zapify.pro/comparison/linktodm-vs-zapify-pro" },
        ],
      }),
      createFAQSchema(faqs),
      createArticleSchema({
        headline: "LinktoDM vs Zapify Pro 2026: Instagram DM Automation Compared",
        description: "Comprehensive comparison of LinktoDM and Zapify Pro for Instagram DM automation in 2026.",
        image: "https://cms.zapify.pro/uploads/OG_image1_f82f5871e2.png",
        author: { name: "Zapify", url: "https://zapify.pro" },
        datePublished: "2026-06-09",
        dateModified: "2026-06-09",
        publisher: {
          name: "Zapify Pro",
          url: "https://zapify.pro",
          logo: "https://zapify.pro/logo-small.png",
          description: "AI-Powered Instagram Comment Management & Automation",
        },
      }),
    ],
  }),
  component: LinktoDMComparisonPage,
})

// ─── Page Component ──────────────────────────────────────────
export function LinktoDMComparisonPage() {
  const { track } = useAnalytics()
  const { openAuth } = useAuth()
  const [openFaq, setOpenFaq] = useState(0)

  const handleCTA = (ctaId: string, buttonText: string) => {
    track("cta_clicked", { cta_id: ctaId, button_text: buttonText })
    openAuth()
  }

  return (
    <div className="min-h-screen relative overflow-hidden bg-white dark:bg-black transition-colors duration-300">
      {/* Radial fade background */}
      <div className="absolute inset-0 bg-white/50 dark:bg-black/20 [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] pointer-events-none" />
      {/* Dotted background */}
      <div className="absolute inset-0 bg-[radial-gradient(circle,#80808015_1px,transparent_1px)] bg-[size:20px_20px] pointer-events-none" />

      <div className="relative z-10 pt-28 sm:pt-36 pb-12">
        {/* ═══ Section 1: Hero ═══ */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 text-center mb-16 sm:mb-20">
          <div className="inline-flex items-center justify-center px-4 py-1.5 rounded-full bg-white dark:bg-neutral-900 border border-neutral-100 dark:border-neutral-800 shadow-sm mb-6">
            <span className="text-neutral-600 dark:text-neutral-300 font-semibold text-xs">Updated June 2026</span>
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-neutral-900 dark:text-white mb-4 sm:mb-6 tracking-tight max-w-5xl mx-auto">
            LinktoDM vs{" "}
            <span className="gradient-text">Zapify Pro</span>
            : The Smarter LinktoDM Alternative for Instagram Creators
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-neutral-600 dark:text-neutral-400 font-medium max-w-2xl mx-auto mb-8 sm:mb-10">
            LinktoDM and Zapify both help creators automate Instagram DMs. But if your goal is to grow your Instagram account, capture leads, understand which content is working, and turn comments into revenue, Zapify is built around a cleaner creator growth workflow with AI-powered replies, brand voice controls, bulk DMs, templates, smart offers, and Zapify insights.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <Button
              onClick={() => handleCTA("CTA_Comparison_Hero", "Start Free No Credit Card")}
              className="bg-primary-gradient text-primary-foreground hover:brightness-[0.97] active:brightness-[0.92] font-bold px-8 py-6 text-base rounded-full shadow-lg"
            >
              Start Free No Credit Card
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              className="px-8 py-6 text-base rounded-full font-semibold border-neutral-200 dark:border-neutral-700"
              onClick={() => {
                document.getElementById("feature-table")?.scrollIntoView({ behavior: "smooth" })
              }}
            >
              See Full Comparison
            </Button>
          </div>
        </section>

        {/* ═══ Ask AI to Explain ═══ */}
        <section className="max-w-3xl mx-auto px-4 sm:px-6 mb-16 sm:mb-20">
          <AskAIExplain
            context="comparison"
            meta={{ competitorName: "LinktoDM", pageUrl: "https://zapify.pro/comparison/linktodm-vs-zapify-pro" }}
            isCard
            isWidget
          />
        </section>

        {/* ═══ Section 2: Quick Answer Box ═══ */}
        <section className="max-w-3xl mx-auto px-4 sm:px-6 mb-16 sm:mb-20" aria-label="Featured summary">
          <article className="rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900/50 p-6 sm:p-8 shadow-sm">
            <p className="text-xs font-bold uppercase tracking-widest text-brand-button mb-3">
              Quick Answer - How does LinktoDM compare to Zapify?
            </p>
            <p className="text-sm sm:text-base text-neutral-600 dark:text-neutral-400 leading-relaxed">
              <strong className="text-neutral-900 dark:text-white">Choose Zapify if you want AI-powered Instagram growth, bulk DM automation, Zapify insights, templates, smart offers, 10+ language support, a $0 free plan for 30 days or 1K contacts, and a possible $200 lifetime access offer.</strong>{" "}
              LinktoDM may only make sense if you specifically need its{" "}
              <strong className="bg-clip-text text-transparent bg-gradient-to-r from-brand-accent to-brand-accent">
                visual flow builder, shared media trigger, backtrack, story mention auto-reply, or Enterprise creator-management dashboard
              </strong>
              . For Instagram-first creators who want AI replies, bulk DMs, Story DMs, email capture, follower growth, lead attribution, templates, smart offers, 10+ languages, and faster setup, Zapify is the better conversion-focused choice.
            </p>
          </article>
        </section>

        {/* ═══ Section 3: Feature Comparison Table ═══ */}
        <section id="feature-table" className="max-w-7xl mx-auto px-4 sm:px-6 mb-16 sm:mb-20">
          <div className="text-center mb-10 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-neutral-900 dark:text-white tracking-tight">
              How does Zapify compare to LinktoDM feature{" "}
              <span className="gradient-text">by feature?</span>
            </h2>
            <Paragraph className="max-w-3xl mx-auto">
              A transparent, side-by-side breakdown of every Instagram automation capability that matters in 2026.
            </Paragraph>
          </div>

          <div className="rounded-2xl border border-neutral-200 dark:border-neutral-800 overflow-hidden shadow-sm bg-white dark:bg-neutral-900/50">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-neutral-50/80 dark:bg-neutral-900/80 border-b border-neutral-200 dark:border-neutral-800">
                    <th className="text-left px-4 sm:px-6 py-4 font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider text-xs w-[40%]">
                      Feature
                    </th>
                    <th className="text-left px-4 sm:px-6 py-4 uppercase tracking-wider text-xs w-[30%] text-green-700 dark:text-green-300">
                      Zapify
                    </th>
                    <th className="text-left px-4 sm:px-6 py-4 font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider text-xs w-[30%]">
                      LinktoDM
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100 dark:divide-neutral-800">
                  {featureCategories.flatMap((cat) => [
                    <tr key={`cat-${cat.category}`} className="bg-neutral-50/70 dark:bg-neutral-900/70">
                      <td colSpan={3} className="px-4 sm:px-6 py-2.5 text-xs font-bold uppercase tracking-widest text-brand-button">
                        {cat.category}
                      </td>
                    </tr>,
                    ...cat.rows.map((row) => (
                      <tr key={`${cat.category}-${row.name}`} className="hover:bg-neutral-50/50 dark:hover:bg-neutral-800/20 transition-colors align-top">
                        <td className="px-4 sm:px-6 py-3.5 font-medium text-neutral-700 dark:text-neutral-300">{row.name}</td>
                        <td className="px-4 sm:px-6 py-3.5">
                          <div className="flex flex-col gap-1.5">
                            <span className="text-sm font-semibold text-neutral-800 dark:text-neutral-100">{typeof row.zapify === "boolean" ? (row.zapify ? "Yes" : "No") : row.zapify}</span>
                            {row.zapifyNote ? <span className="text-xs text-green-700 dark:text-green-300">{row.zapifyNote}</span> : null}
                          </div>
                        </td>
                        <td className="px-4 sm:px-6 py-3.5">
                          <div className="flex flex-col gap-1.5">
                            <span className="text-sm font-semibold text-neutral-700 dark:text-neutral-200">{typeof row.linktodm === "boolean" ? (row.linktodm ? "Yes" : "No") : row.linktodm}</span>
                            {row.linktodmNote ? <span className="text-xs text-red-600 dark:text-red-300">{row.linktodmNote}</span> : null}
                          </div>
                        </td>
                      </tr>
                    )),
                  ])}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* ═══ Section 4: Pricing Breakdown ═══ */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 mb-16 sm:mb-20">
          <div className="text-center mb-10 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-neutral-900 dark:text-white tracking-tight">
              LinktoDM's pricing vs{" "}
              <span className="gradient-text">Zapify's creator-first pricing</span>
            </h2>
            <Paragraph className="max-w-2xl mx-auto">
              LinktoDM has a useful free plan, but Zapify has the stronger conversion story for creators who want to test and grow: $0 for 30 days or 1K contacts, paid plans positioned around unlimited DMs, and a possible $200 lifetime access offer.
            </Paragraph>
          </div>

          <div className="rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900/50 overflow-hidden">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-neutral-50/80 dark:bg-neutral-900/80 border-b border-neutral-200 dark:border-neutral-800">
                  <th className="text-left px-4 sm:px-5 py-3 text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Plan stage</th>
                  <th className="text-left px-4 sm:px-5 py-3 text-xs font-semibold uppercase tracking-wide text-green-700 dark:text-green-300">Zapify</th>
                  <th className="text-left px-4 sm:px-5 py-3 text-xs font-semibold uppercase tracking-wide text-neutral-600 dark:text-neutral-300">LinktoDM</th>
                </tr>
              </thead>
              <tbody>
                {pricingTiers.map((tier) => (
                  <tr key={tier.label} className="align-top border-b last:border-b-0 border-neutral-100 dark:border-neutral-800/80">
                    <td className="px-4 sm:px-5 py-4 text-sm font-semibold text-neutral-800 dark:text-neutral-100">{tier.label}</td>
                    <td className="px-4 sm:px-5 py-4 text-lg font-black tracking-tight text-green-700 dark:text-green-300">{tier.zapify}</td>
                    <td className="px-4 sm:px-5 py-4 text-lg font-black tracking-tight text-neutral-700 dark:text-neutral-100">{tier.linktodm}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* ═══ Lifetime Access Pricing ═══ */}
        <LifetimeAccessPricing
          spotsLeft={87}
          totalSpots={100}
          price={200}
          originalMonthlyPrice={20}
          onGetAccess={() => handleCTA("CTA_LinktoDM_Lifetime_Pricing", "Claim Lifetime Access")}
        />

        {/* ═══ Section 5: Full Platform vs Basic Tool ═══ */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 mb-16 sm:mb-20">
          <div className="text-center mb-10 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-neutral-900 dark:text-white tracking-tight">
              Growth Platform vs{" "}
              <span className="gradient-text">Automation Builder</span>
            </h2>
            <Paragraph className="max-w-2xl mx-auto">
              LinktoDM is an automation builder. Zapify is the better fit when the creator wants an Instagram growth system: comments to DMs, DMs to leads, leads to sales, and clear attribution for what Zapify helped grow.
            </Paragraph>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Zapify Platform */}
            <div className="rounded-2xl border-2 border-green-200 dark:border-green-800/50 bg-white dark:bg-neutral-900/50 p-6 sm:p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                  <Zap className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
                <h3 className="text-lg font-bold text-green-600 dark:text-green-400">Zapify - Growth Platform</h3>
              </div>

              <p className="text-neutral-600 dark:text-neutral-400 text-sm mb-3">
                Focused on growth outcomes:
              </p>
              <div className="bg-green-50 dark:bg-green-900/20 rounded-lg px-4 py-3 text-sm text-green-700 dark:text-green-300 mb-4">
                <Check className="inline w-4 h-4 mr-1" /> AI replies with brand voice → bulk DMs → Zapify insights → followers & leads gained
              </div>

              <p className="text-neutral-600 dark:text-neutral-400 text-sm mb-3">
                Faster campaign launches:
              </p>
              <div className="bg-green-50 dark:bg-green-900/20 rounded-lg px-4 py-3 text-sm text-green-700 dark:text-green-300">
                <Check className="inline w-4 h-4 mr-1" /> Templates + smart offers → launch lead magnets, coupons, booking links in minutes
              </div>

              <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-4">
                Focused Instagram growth. AI-powered. Clear attribution.
              </p>
            </div>

            {/* LinktoDM Builder */}
            <div className="rounded-2xl border-2 border-red-200 dark:border-red-800/50 bg-white dark:bg-neutral-900/50 p-6 sm:p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-red-500 dark:text-red-400" />
                </div>
                <h3 className="text-lg font-bold text-red-500 dark:text-red-400">LinktoDM - Automation Builder</h3>
              </div>

              <p className="text-neutral-600 dark:text-neutral-400 text-sm mb-3">
                Emphasis on flow builder and controls:
              </p>
              <div className="bg-red-50 dark:bg-red-900/20 rounded-lg px-4 py-3 text-sm text-red-700 dark:text-red-300 mb-4">
                <X className="inline w-4 h-4 mr-1" /> Visual node-based flows, queue management, rate limiting — more complex setup
              </div>

              <p className="text-neutral-600 dark:text-neutral-400 text-sm mb-3">
                No Zapify-style growth attribution:
              </p>
              <div className="bg-red-50 dark:bg-red-900/20 rounded-lg px-4 py-3 text-sm text-red-700 dark:text-red-300">
                <X className="inline w-4 h-4 mr-1" /> No follower/lead attribution — tracks performance, not growth outcomes
              </div>

              <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-4">
                Broader automation builder. More complex. No growth attribution.
              </p>
            </div>
          </div>
        </section>

        {/* ═══ Section 6: FAQ ═══ */}
        <section className="w-full py-12 sm:py-20 px-4 sm:px-6 border-b border-neutral-100 dark:border-white/5">
          <div className="max-w-5xl mx-auto">
            <div className="mb-8 text-center sm:mb-12">
              <h2 className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-bold text-[#181C31] dark:text-white tracking-tight">
                Frequently <span className="gradient-text">Asked Questions</span>
              </h2>
              <p className="text-sm sm:text-base md:text-xl text-[#64657E] dark:text-gray-400 font-medium max-w-2xl mx-auto mt-3 sm:mt-5">
                Everything you need to know about LinktoDM vs Zapify
              </p>
            </div>

            <div className="space-y-3">
              {faqs.map((faq, index) => {
                const isOpen = openFaq === index
                return (
                  <div key={faq.question} className="rounded-2xl border border-border/50 bg-card/90 px-4 py-3.5 sm:px-6 sm:py-4 dark:bg-card/80">
                    <button type="button" onClick={() => setOpenFaq((prev) => (prev === index ? -1 : index))} className="flex w-full items-center justify-between gap-3 text-left">
                      <div className="flex items-center gap-3">
                        <span className="text-base leading-none">{["⚡", "🔍", "🔄", "📦", "🆓", "🆓", "🤖", "🤖", "💰", "🔄", "🏢"][index] ?? "⚡"}</span>
                        <p className="text-base font-semibold leading-snug text-foreground sm:text-[1.15rem]">{faq.question}</p>
                      </div>
                      <span className="inline-flex size-8 shrink-0 items-center justify-center rounded-full text-muted-foreground">{isOpen ? "−" : "+"}</span>
                    </button>
                    <div className={`grid transition-all duration-300 ${isOpen ? "mt-2.5 grid-rows-[1fr] opacity-100 sm:mt-3" : "grid-rows-[0fr] opacity-0"}`}>
                      <p className="overflow-hidden pr-3 text-[13px] leading-relaxed text-muted-foreground sm:pr-8 sm:text-[0.95rem]">{faq.answer}</p>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </section>

        {/* ═══ Section 7: Bottom CTA ═══ */}
        <section className="relative overflow-hidden py-16 sm:py-24 px-4 sm:px-6">
          {/* Gradient blob */}
          <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-brand-accent/10 rounded-full blur-3xl" />
          </div>

          <div className="relative max-w-2xl mx-auto text-center">
            <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold text-neutral-900 dark:text-white mb-4 sm:mb-6 tracking-tight">
              Ready to grow with <span className="gradient-text">focused Instagram automation?</span>
            </h2>
            <p className="text-base sm:text-lg text-neutral-600 dark:text-neutral-400 mb-8 sm:mb-10 max-w-xl mx-auto">
              AI-powered replies, bulk DM automation, growth attribution, and lifetime access. Try Zapify free and see the difference a focused growth platform makes.
            </p>

            <Button
              onClick={() => handleCTA("CTA_Comparison_Bottom", "Start Free with Zapify")}
              className="bg-primary-gradient text-primary-foreground hover:brightness-[0.97] active:brightness-[0.92] font-bold px-10 py-6 text-lg rounded-full shadow-lg"
            >
              Start Free with Zapify
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>

            <p className="mt-4 text-sm text-neutral-500 dark:text-neutral-400">
              30-day free plan &middot; No credit card &middot; 1-minute setup
            </p>
          </div>
        </section>

        {/* Other Comparisons */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16 border-b border-neutral-100 dark:border-white/5">
          <div className="text-center mb-8">
            <h2 className="text-xl sm:text-2xl font-bold text-neutral-900 dark:text-white">
              Compare Zapify vs <span className="gradient-text">other tools</span>
            </h2>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            {[
              { label: "Zapify vs ManyChat", href: "/comparison/manychat-vs-zapify-pro" },
              { label: "Zapify vs LinkDM", href: "/comparison/linkdm-vs-zapify-pro" },
              { label: "Zapify vs Zorcha", href: "/comparison/zorcha-vs-zapify-pro" },
              { label: "Zapify vs Inrō", href: "/comparison/inro-vs-zapify-pro" },
              { label: "Zapify vs CreatorFlow", href: "/comparison/creatorflow-vs-zapify-pro" },
              { label: "Zapify vs InstantDM", href: "/comparison/instantdm-vs-zapify-pro" },
            ].map((link) => (
              <a key={link.href} href={link.href} className="px-5 py-2.5 rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900/50 text-sm font-semibold text-neutral-700 dark:text-neutral-300 hover:border-neutral-300 dark:hover:border-neutral-700 hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-all duration-200">
                {link.label}
              </a>
            ))}
          </div>
        </section>

        {/* Disclaimer */}
        <ContentSection className="max-w-4xl mx-auto px-4 sm:px-6 pb-8">
          <p className="text-xs text-neutral-400 dark:text-neutral-500 text-center">
            LinktoDM is a trademark of its respective owner. Zapify is not affiliated with, endorsed by, or sponsored by LinktoDM. Feature and pricing information is based on publicly available information and Zapify research as of June 6, 2026. Always check each provider's website for current pricing and plan details.
          </p>
        </ContentSection>
      </div>
    </div>
  )
}
